<template>
  <div v-if="loading" class="text-center mt-5 font-2xl text-muted">
    <i class="fal fa-spinner fa-spin" /> Test Process Form Data Loading...
  </div>

  <div v-else class="animated fadeIn">
    <b-form @submit.prevent="submitForm">

      <b-row class="mt-4">
        <b-col>

          <b-form-group label="Name" :label-cols="2">
            <b-form-input
              id="name"
              v-model="form_data.name"
              :state="!$v.form_data.name.$invalid"
              type="text"
              placeholder="Process Name"
            />
            <b-form-invalid-feedback v-if="$v.form_data.name.$error">
              Required
            </b-form-invalid-feedback>
          </b-form-group>

          <b-form-group label="Description" :label-cols="2">
            <b-form-input
              id="description"
              v-model="form_data.description"
              type="text"
              placeholder="Process Description"
            />
          </b-form-group>

        </b-col>
      </b-row>

      <hr>

      <b-row class="mt-3">
        <b-col>
          <div class="mb-3">
            <div class="display-4 font-3xl text-left">
              Export Configuration
            </div>
          </div>

          <div class="p-3 bg-light">
            <b-form-group label="Export Type" :label-cols="2">
              <b-alert show variant="info">
                <i class="fas fa-info-circle" />
                The spreadsheet that the process uses to export & import result data
              </b-alert>
              <b-form-input
                id="sample_type"
                v-model="form_data.export_type"
                type="text"
                disabled
              />
            </b-form-group>
          </div>

          <div class="p-3">
            <b-form-group label="Sheet" :label-cols="2">
              <b-alert show variant="info">
                <i class="fas fa-info-circle" />
                The name of the spreadsheet's page/sheet that the process uses to export & import result data
              </b-alert>
              <b-form-input
                id="export_sheet"
                v-model="form_data.export_sheet"
                type="text"
              />
            </b-form-group>
          </div>

        </b-col>
      </b-row>

      <hr>

      <b-row class="mt-3 mb-3">
        <b-col>
          <div class="mb-3 d-flex justify-content-between">
            <div class="display-4 font-3xl text-left">
              Export Column Configuration
            </div>

            <b-button class="float-right" variant="success" @click="addColumn">
              <i class="fal fa-plus" />
            </b-button>
          </div>

          <b-alert show variant="info">
            <i class="fas fa-info-circle" />
            The spreadsheet that the process uses to export & import result data
          </b-alert>

          <div v-for="(item, index) in form_data.column_export" :key="index" class="p-3 bg-light mb-3">
            <b-form-group label="Column" :label-cols="2">
              <b-form-input
                id="column"
                v-model="item.column"
                type="text"
              />
            </b-form-group>

            <b-form-group label="Type" :label-cols="2">
              <b-form-select
                id="type"
                v-model="item.type"
                :options="column_type_opts"
              >
                <template #first>
                  <option :value="null" disabled>
                    Select Type
                  </option>
                </template>
              </b-form-select>
            </b-form-group>

            <b-form-group label="Type Attribute" :label-cols="2">
              <b-form-select
                id="type"
                v-model="item.type_id"
                :options="getTypeAttributeOpts(item.type)"
              >
                <template #first>
                  <option :value="null" disabled>
                    Select Type Attribute
                  </option>
                </template>
              </b-form-select>
            </b-form-group>
          </div>

          <b-button block variant="success" @click="addColumn">
            <i class="fa fa-plus" /> Add Column
          </b-button>

        </b-col>
      </b-row>

      <!-- validation error -->
      <b-row v-if="invalid" class="mt-4">
        <b-col>
          <b-alert variant="danger" show>
            <i class="fa fa-exclamation-triangle" /> Please review the form and complete all required fields.
          </b-alert>
        </b-col>
      </b-row>

      <b-row>
        <b-col>
          <b-button
            type="submit"
            class="my-4 float-right py-3 px-5"
            variant="success"
          >
            <i class="fal fa-save" /> Save
          </b-button>
        </b-col>
      </b-row>

    </b-form>
  </div>
</template>

<script>
import { getCollection } from '@/shared/fetch-utils'
import { required } from 'vuelidate/lib/validators'
import { validationMixin } from 'vuelidate/src'

export default {
  name: 'WorkflowTestProcessForm',
  components: {
  },
  mixins: [validationMixin],
  props: {
    sample_type: {
      type: Object,
      default: () => null
    },
    test_process: {
      type: Object,
      default: () => null
    }
  },
  data () {
    return {
      loading: true,

      form_data: {
        name: null,
        description: null,
        export_type: null,
        export_sheet: null,
        column_export: [
          {
            column: null,
            type: null,
            type_id: null
          }
        ]
      },

      managed_list: null,

      column_type_opts: [
        { text: 'Attribute', value: 'attribute' },
        { text: 'Analyte', value: 'analyte' }
      ]
    }
  },
  validations: {
    form_data: {
      name: { required }
    }
  },
  computed: {
    invalid() {
      return this.$v && this.$v.$dirty && this.$v.$invalid
    },

    managed_list_opts () {
      return this.managed_list && this.managed_list.models.map(({ id, description }) => {
        return {
          value: id,
          text: description
        }
      })
    }
  },
  async mounted () {
    this.loading = true

    this.managed_list = await getCollection({
      collection_type: 'managed_list',
      limit: 'all',
      message: this.$bvToast
    })

    this.init()
  },
  methods: {
    initFormData () {
      if (this.test_process && this.test_process.id) {
        this.form_data.name = this.test_process.name
        this.form_data.description = this.test_process.description

        if (this.test_process.export_map) {
          this.form_data.export_type = this.test_process.export_map.type
          this.form_data.export_sheet = this.test_process.export_map.sheet

          this.form_data.column_export = this.test_process.export_map.column_export ? this.test_process.export_map.column_export : []
        }
      }
    },

    init () {
      if (this.sample_type) {
        this.form_data.export_type = this.sample_type.name
      }

      this.initFormData()
      this.loading = false
    },

    getTypeAttributeOpts (type) {
      if (type === 'analyte' && this.sample_type.analytes) {
        return this.sample_type.analytes.map(({ id, name }) => {
          return {
            value: id,
            text: name
          }
        })
      } else if (type === 'attribute') {
        return this.managed_list_opts
      }

      return []
    },

    addColumn () {
      this.form_data.column_export.push({
        column: null,
        type: null,
        type_id: null
      })
    },

    submitForm () {
      this.$v.$touch()
      if (this.invalid) return

      // Filter column_export array to include objects where at least one property is not null
      const validColumnExports = this.form_data.column_export.filter(item => {
        return item.column != null || item.type != null || item.type_id != null;
      })

      return this.$emit('submit', {
        name: this.form_data.name,
        description: this.form_data.description,
        sample_type_id: this.sample_type.id,
        export_map: {
          type: this.form_data.export_type,
          sheet: this.form_data.export_sheet,
          column_export: validColumnExports
        }
      })
    }
  }
}
</script>
